import { Trans, useLingui } from '@lingui/react'
import { shape, string } from 'prop-types'
import { compose } from 'redux'

import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts'
import doctorOnlineIllustration from 'views/assets/images/misc/Doctor online_RC@2x.png'
import erxSaeTransparentIllustration from 'views/assets/images/misc/eRX_RC@2x.png'
import nowUnknownLocationIllustration from 'views/assets/images/misc/NOW unknown location_RC@2x.png'
import Button from 'views/components/atoms/Button'
import IconInfo from 'views/components/atoms/Icon/IconInfo'
import IconPrescription from 'views/components/atoms/Icon/IconPrescription'
import Image from 'views/components/atoms/Image'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import { ODS_TRANSITION_MODAL_ID } from 'views/components/organisms/OdsTransitionModal/names'
import Tabs from 'views/components/organisms/Tabs'
import { ODS_MODAL_PAGE_BTN_ID_REMAINDER_ZAVA_CLICK } from 'views/consts'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  i18nType,
  mountPointsType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'
import './OdsZavaModal.scss'

import HexNumber from './HexNumber'

const getTabCont1 = ({ i18n }) => (
  <div className="a-box--medium a-box a-box--light">
    <div className="u-margin--top">
      <center>
        <img
          width="108"
          src={doctorOnlineIllustration}
          alt={i18n._(
            'pdp.buyboxwithvariants.odsModal.onlineConsultation.image.alt'
          )}
        />
      </center>
    </div>

    <h3 className="u-padding-x-large--top u-no-margin u-font-weight--semi-bold">
      <Trans id="pdp.buyboxwithvariants.odsModal.tabs.tab1.heading" />
    </h3>
    <h4 className="u-padding--top u-font-weight--semi-bold">
      <Trans id="pdp.buyboxwithvariants.odsModal.tabs.tab1.stepsHeader" />
    </h4>

    <div className="l-flex l-flex--start u-margin-small--bottom o-OdsModalContent__step">
      <div className="u-margin-small--right">
        <HexNumber number={1} />
      </div>
      <div className="">
        <h4 className="u-no-margin u-color--greyLight-dark">
          <Trans id="pdp.buyboxwithvariants.odsModal.process.step1" />
        </h4>
      </div>
    </div>
    <div className="l-flex l-flex--start u-margin-small--bottom o-OdsModalContent__step">
      <div className="u-margin-small--right">
        <HexNumber number={2} />
      </div>
      <div className="">
        <h4 className="u-no-margin u-color--greyLight-dark">
          <Trans id="pdp.buyboxwithvariants.odsModal.process.step2" />
        </h4>
      </div>
    </div>
    <div className="l-flex l-flex--start u-margin-small--bottom o-OdsModalContent__step">
      <div className="u-margin-small--right">
        <HexNumber number={3} />
      </div>
      <div className="">
        <h4 className="u-no-margin u-color--greyLight-dark">
          <Trans
            id="pdp.buyboxwithvariants.odsModal.process.step3"
            components={{
              sup: <sup />,
            }}
          />
        </h4>
      </div>
    </div>

    <h4 className="u-padding-x-large--top u-no-margin u-font-weight--semi-bold">
      <Trans id="general.modal.title.important" />
    </h4>
    <h4 className="u-padding--top">
      <Trans
        components={{
          0: <p className="u-no-margin u-color--greyLight-dark " />,
          1: <br />,
        }}
        id="pdp.buyboxwithvariants.odsModal.tabs.tab1.onlineDoctorNotice.fock"
      />
    </h4>
    <TextWithIcon icon={<IconPrescription size={'small'} />} alignIconToTop>
      <div className="u-padding-xx-small--top u-color--dark u-padding--left">
        <h4 className="u-padding--bottom u-no-margin u-font-weight--semi-bold">
          <Trans id="pdp.buyboxwithvariants.odsModal.onlineDoctor.billingAsPrivate.heading" />
        </h4>
        <Trans
          className="u-color--greyLight-dark"
          components={{
            0: <p className="u-no-margin u-color--greyLight-dark" />,
            1: <br />,
          }}
          id="pdp.buyboxwithvariants.odsModal.onlineDoctor.billingAsPrivate.description.fock"
        />
      </div>
    </TextWithIcon>
  </div>
)

const getTabCont2 = ({ mountPoints, i18n }) => (
  <div className="a-box--medium a-box a-box--light">
    <div className="u-margin--top">
      <center>
        <img
          width="108"
          src={nowUnknownLocationIllustration}
          alt={i18n._('pdp.buyboxwithvariants.odsModal.tabs.tab2.title')}
        />
      </center>
    </div>

    <h3 className="u-padding-x-large--top u-no-margin u-font-weight--semi-bold">
      <Trans id="pdp.buyboxwithvariants.odsModal.tabs.localDoctor.heading" />
    </h3>

    <h4 className="u-padding-large--top u-color--greyLight-dark">
      <Trans id="pdp.buyboxwithvariants.odsModal.tabs.localDoctor.description" />
    </h4>

    <LinkWithIcon
      className="u-font-weight--semi-bold"
      icon={<IconInfo className="u-padding-x-small--right" />}
      href={mountPoints.getRedeemRecipeFunnel()}
    >
      <Trans id="account.orders.details.collapsible.label.information" />
    </LinkWithIcon>
  </div>
)

const isSearchPage = pageType => pageType === PAGE_TYPE_SEARCH_RESULTS

const setHrefUrl = (pageType, indicationUrl) =>
  isSearchPage(pageType) ? ' ' : indicationUrl

const getPageBasedBtnZavaClickId = pageType =>
  `${pageType}-${ODS_MODAL_PAGE_BTN_ID_REMAINDER_ZAVA_CLICK}`

const OdsModalContent = ({
  mountPoints,
  indicationUrl,
  publicRuntimeConfig: {
    pageProperties: { pageType },
  },
}) => {
  const { i18n } = useLingui()
  const ButtonRequestComponent =
    pageType === PAGE_TYPE_SEARCH_RESULTS ? (
      <Trans
        className="u-no-margin"
        id={'onlinedoctorservice.receiptonline.cta.text'}
      />
    ) : (
      <Trans
        className="u-no-margin"
        id={'pdp.buyboxwithvariants.odsModal.button.accesstomedication'}
      />
    )

  return (
    <>
      <ul className="a-list-reset l-flex@medium">
        <li className="l-flex__start">
          <div className="u-margin--right@medium">
            <Image
              alt={i18n._('pdp.buyboxwithvariants.odsModal.image.alt')}
              src={erxSaeTransparentIllustration}
              width={162}
              className="o-odsModal-Image--center u-margin--sides"
            />
          </div>
        </li>
        <li>
          <div className="u-padding--left">
            <h2 className="a-h2">
              <Trans id={'pdp.buyboxwithvariants.odsModal.header'} />
            </h2>
            <div className="l-grid u-margin-small--bottom">
              <div className="l-grid__item l-col--11-of-12">
                <h4 className="u-color--greyLight-dark">
                  <Trans id="pdp.buyboxwithvariants.odsModal.main.info.desc" />
                </h4>
              </div>
            </div>
          </div>
          <div className="u-padding--bottom o-OdsModalContent__wrapper-button-online-doctor">
            {isSearchPage(pageType) ? (
              <ModalShowButton
                component={Button}
                // this content is used in Modal
                // so we close the current modal when we open the new one
                data-a11y-dialog-hide
                id={getPageBasedBtnZavaClickId(pageType)}
                name="buybox-large_online-doctor-service"
                modalId={ODS_TRANSITION_MODAL_ID}
              >
                {ButtonRequestComponent}
              </ModalShowButton>
            ) : (
              <Button
                id={getPageBasedBtnZavaClickId(pageType)}
                name="ods-modal-go-to-zava"
                data-clientside-hook="ods-modal-go-to-zava"
                href={setHrefUrl(pageType, indicationUrl)}
              >
                {ButtonRequestComponent}
              </Button>
            )}
          </div>
          <div className="o-odsModal-strike">
            <span className="u-color--grey">oder</span>
          </div>

          <div className="u-padding--top">
            <Button
              id={`${pageType}-rx-nearByClick`}
              name="ods-find-doctor-nearby"
              data-clientside-hook="ods-modal-find-doctor-nearby"
              target="_blank"
              href="https://arztsuche.116117.de/pages/arztsuche.xhtml"
              className="o-OdsModalContent__button-find-doctor-nearby"
            >
              <Trans
                className="u-no-margin"
                id={'pdp.buyboxwithvariants.odsModal.button.localsearch'}
              />
            </Button>
          </div>
        </li>
      </ul>

      <div className="u-margin-xx-large--top">
        <Tabs
          items={[
            {
              title: (
                <Trans id="pdp.buyboxwithvariants.odsModal.tabs.tab1.title" />
              ),
              content: getTabCont1({ i18n }),
            },
            {
              title: (
                <Trans id="pdp.buyboxwithvariants.odsModal.tabs.tab2.title" />
              ),
              content: getTabCont2({ mountPoints, i18n }),
            },
          ]}
          tabId="recipeType"
          noContentPadding
        />
      </div>
    </>
  )
}

OdsModalContent.propTypes = {
  i18n: i18nType.isRequired,
  mountPoints: mountPointsType.isRequired,
  indicationUrl: string,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
}

export default compose(
  withShopAndRuntimeConfig,
  withMountPoints
)(OdsModalContent)
